<script>
  import Button from '@leaflink/stash/Button.vue';
  import { RouterLink } from 'vue-router';

  import { ROUTE_NAMES } from '@/constants/routeNames';

  import { buyerData, sellerData } from './dob-data';

  export default {
    name: 'JoinTodayDigitalOnboarding',

    components: {
      Button,
      RouterLink,
    },

    emits: ['cta-click'],

    props: {
      company: {
        type: Object,
        required: true,
      },
    },

    computed: {
      isSeller() {
        return this.company.seller;
      },

      heroDescription() {
        return this.isSeller
          ? "Power your business forward with a B2B payment solution that pays you upfront and protects you from risk. We'll help you get paid faster, so you can gain access to working capital and streamline your entire accounting process, all from a single platform."
          : 'With LeafLink Payments, you can order more, pay later, and keep more cash on hand. This means reducing your procurement expenses and investing back into the growth of your business.';
      },

      heroHeading() {
        return this.isSeller ? 'Quick & secure digital payments' : 'Buy Now, Pay Later';
      },

      sections() {
        return this.isSeller ? sellerData : buyerData;
      },

      dobUrl() {
        const { buyer, slug } = this.company;
        const type = buyer ? 'buyer' : 'seller';

        // This value is being added to the route via an AppCue
        const sfcid = this.$route.query.sfcid;

        return {
          name: ROUTE_NAMES.PAYMENTS_SIGN_UP,
          params: { slug, type, sfcid },
        };
      },
    },

    methods: {},
  };
</script>

<template>
  <div :class="$style.wrapper">
    <header class="flex align-middle bg-purple" :class="$style.header">
      <div class="container flex flex-col text-center align-center" :class="[$style.hero, $style['container--narrow']]">
        <img
          :src="'/static/img/branding/logoFullPaymentsWhite.svg'"
          :alt="$t('common.llPayments')"
          :class="$style['payments-logo']"
        />
        <h1 class="ll-mt-4 ll-mb-2 text-white font-weight-bold" :class="$style['hero-heading']">{{ heroHeading }}</h1>
        <h3 class="ll-mb-4 text-white" :class="$style['hero-subheading']">Get onboarded in under two minutes!</h3>
        <p class="ll-mb-4 text-white" :class="$style['hero-description']">{{ heroDescription }}</p>
        <RouterLink :to="dobUrl" v-slot="{ href }" custom>
          <Button
            class="font-weight-medium"
            rel="noopener"
            target="_blank"
            :class="$style['hero-button']"
            :href="href"
            @click="$emit('cta-click')"
          >
            Join today!
          </Button>
        </RouterLink>
      </div>
    </header>
    <div class="container" :class="$style.content">
      <section
        v-for="(section, i) in sections"
        class="relative"
        :class="[$style.section, { [$style.alt]: i % 2 !== 0 }]"
        :key="`section-${i}`"
        :style="{
          '--img-height': section.image['1x'].height,
          '--img-width': section.image['1x'].width,
        }"
      >
        <div class="relative" :class="$style.card">
          <img
            :class="$style.graphic"
            :sizes="`(min-width: 641px) ${section.image['1x'].width}px, 100vw`"
            :src="section.image['1x'].path"
            :srcset="`
              ${section.image['1x'].path} ${section.image['1x'].width}w,
              ${section.image['2x'].path} ${section.image['2x'].width}w
            `"
          />
        </div>
        <div :class="$style.info">
          <div class="inline-block" :class="$style['info-block']">
            <h2 v-html="section.heading" class="ll-mb-1 font-weight-bold" :class="$style['section-heading']"></h2>
            <p v-html="section.description" class="ll-mb-0" :class="$style['section-description']"></p>
          </div>
        </div>
      </section>
    </div>
    <footer class="bg-purple flex align-center align-middle" :class="$style.footer">
      <div class="container text-center" :class="$style['container--narrow']">
        <h2 class="text-white font-weight-bold" :class="$style['footer-heading']">
          A seamless, simple, intuitive way to manage transactions
        </h2>
        <RouterLink :to="dobUrl" v-slot="{ href }" custom>
          <Button
            class="font-weight-medium"
            rel="noopener"
            target="_blank"
            :class="$style['hero-button']"
            :href="href"
            @click="$emit('cta-click')"
          >
            Join today!
          </Button>
        </RouterLink>
      </div>
    </footer>
  </div>
</template>

<style lang="scss" module>
  $section-margin-sm: 72;
  $section-margin-lg: 100;

  :global(#app-content) {
    padding-bottom: 0;
  }

  .wrapper {
    --section-margin: #{$section-margin-sm};

    background: var(--color-ice-100);
  }

  .header {
    background: url('/static/img/digital-onboarding/header-bg.svg') no-repeat center center;
    background-size: cover;
  }

  .hero {
    padding-bottom: ($section-margin-sm * 2px);
    padding-top: 70px;
  }

  .hero-heading {
    font-size: 2.625rem;
    line-height: 2.875rem;

    @include breakpoint('md') {
      font-size: 3rem;
      line-height: 3.3125rem;
    }
  }

  .hero-subheading {
    font-size: 1.5rem;
    line-height: 2rem;
  }

  .hero-description {
    font-size: 1rem;
    line-height: 1.5rem;

    @include breakpoint('md') {
      font-size: 1.25rem;
      line-height: 1.75rem;
    }
  }

  .hero-button {
    border-radius: 100px;
    font-size: 1.5rem;
    line-height: 3.4375rem;
    margin: 0 auto;
    max-width: 400px;
    width: 100%;
  }

  .payments-logo {
    height: 20px;
    opacity: 0.5;
  }

  // narrow container, as per design
  .container--narrow {
    max-width: 960px;
  }

  // narrow content container, as per design
  .content {
    max-width: 1148px;
  }

  .section {
    --half-img-height: var(--img-height) / 2;

    margin-bottom: calc(var(--section-margin) * 1px);

    &.alt {
      background: $royal-100;

      @include breakpoint('lg') {
        background: none;
        margin-top: initial;
      }
    }

    & + &.alt {
      // Add margin here to account for difference in collapsing margins with section above
      margin-top: calc((var(--half-img-height) + var(--section-margin)) * 1px);
    }

    &:first-child {
      margin-top: $section-margin-sm * -1px;
    }

    @include breakpoint('lg') {
      --section-margin: #{$section-margin-lg};

      align-items: center;
      display: flex;

      & + &.alt {
        margin-top: initial;
      }
    }
  }

  .section-heading {
    font-size: 1.5rem;
    line-height: 2rem;

    @include breakpoint('md') {
      font-size: 2rem;
      line-height: 1.4375em;
    }

    @include breakpoint('lg') {
      // Scale heading text with container to prevent container from growing vertically due to wrapping text
      // https://css-tricks.com/linearly-scale-font-size-with-css-clamp-based-on-the-viewport/
      // minViewportWidth: 961
      // maxViewportWidth: 1480 (tweaked from 1352)
      font-size: clamp(1.5rem, calc(-0.6016rem + 3.0829vw), 2rem);
    }
  }

  .section-description {
    font-size: 1.25rem;
    line-height: 1.75rem;
  }

  .card {
    background: #fff;
    border-radius: 20px;
    margin: 0 auto space(4);
    max-width: calc(var(--img-width) * 1px);

    &,
    &::before {
      border-radius: 20px;
    }

    &::before {
      bottom: 0;
      box-shadow: 0 0 4px 0 rgba(#eee, 0.06), 0 0 40px 0 #eee;
      content: '';
      left: 0;
      mix-blend-mode: multiply;
      position: absolute;
      right: 0;
      top: 0;
      z-index: 300;
    }

    .alt & {
      margin-bottom: calc(var(--half-img-height) * -1px + #{space(4)});
      position: relative;
      top: calc(var(--half-img-height) * -1px);
    }

    @include breakpoint('lg') {
      margin-bottom: initial;

      .alt & {
        margin-bottom: initial;
        order: 1;
        top: initial;
      }
    }
  }

  .graphic {
    max-width: 100%;
  }

  .info {
    text-align: center;
    width: 100%;

    .alt & {
      padding-bottom: space(5);
    }

    @include breakpoint('lg') {
      flex: 1;
      padding: space(5) space(3);
      text-align: right;

      .alt & {
        background: $royal-100;
        padding-left: space(5);
        text-align: left;
      }
    }
  }

  .info-block {
    @include breakpoint('lg') {
      max-width: 80%;
      min-width: 480px;
      text-align: left;
    }
  }

  .footer {
    padding: 60px 0;

    @include breakpoint('md') {
      padding: 120px 0;
    }

    @include breakpoint('lg') {
      padding: 180px 0;
    }
  }

  .footer-heading {
    font-size: 2.25rem;
    letter-spacing: -0.02em;
    line-height: 2.5rem;
    margin-bottom: 62px;

    @include breakpoint('md') {
      margin-bottom: $section-margin-sm * 1px;
    }
  }
</style>
